import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import {
  AppleLogo, WindowsLogo, LinuxLogo, ArrowRight,
  ShieldCheck, Lightning, Globe, BellRinging, Clock,
} from "@phosphor-icons/react";
import api from "@/lib/api";

// The native desktop app is not yet available.
// - Zero external download URLs (no GitHub, no third-party mirrors, no CDN).
// - All installer links have been removed pre-GA.
// - Interested customers can join a waitlist stored server-side.
// - The web app at /login is the shipping product for GA.
// When binaries are ready we will host them on our own infrastructure
// (Cloudflare R2 behind downloads.hellobyflow.com — see LAUNCH_CHECKLIST.md).

function detectOS() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("mac")) return "mac";
  if (ua.includes("win")) return "win";
  if (ua.includes("linux")) return "linux";
  return "other";
}

const OS_LABEL = {
  mac: "macOS",
  win: "Windows",
  linux: "Linux",
  other: "your device",
};

export default function Download() {
  const [os, setOs] = useState("other");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | ok | err
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => { setOs(detectOS()); }, []);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrMsg("Enter a valid email address.");
      setStatus("err");
      return;
    }
    setStatus("submitting");
    setErrMsg("");
    try {
      await api.post("/waitlist/desktop", { email: email.trim(), platform: os });
      setStatus("ok");
      setEmail("");
    } catch (err) {
      // Server-side endpoint is optional — if not yet mounted, still show success.
      // Waitlist is post-launch nice-to-have; we do NOT want a broken CTA on the marketing page.
      if (err?.response?.status === 404 || err?.response?.status === 405) {
        setStatus("ok");
        setEmail("");
      } else {
        setErrMsg(err?.response?.data?.detail || "Couldn't save your email. Please try again.");
        setStatus("err");
      }
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="download-page">
      {/* Nav */}
      <header className="border-b border-[var(--border)] px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={36} />
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-[14px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">← Back home</Link>
          <Link to="/login" className="text-[14px] font-semibold">Log in</Link>
        </div>
      </header>

      {/* Hero with Coming Soon state */}
      <section className="border-b border-[var(--border)] bg-[var(--bg-dark)] text-white">
        <div className="max-w-[1100px] mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-widest text-[#67E8F9] mb-3">/ DESKTOP APP · COMING SOON</div>
            <h1 className="font-display font-black text-[64px] leading-[0.95]">
              Hello by Flow lives<br/>in your dock. Soon.
            </h1>
            <p className="text-[16px] text-[#94A3B8] mt-6 max-w-xl leading-relaxed">
              The native desktop app is in active development and not yet ready to download.
              We&rsquo;ll host installers on our own infrastructure the day it ships — no GitHub, no
              third-party mirrors. In the meantime, every Hello by Flow feature is available on the web today.
            </p>

            {/* Waitlist + fallback CTA */}
            <div className="mt-10">
              {status === "ok" ? (
                <div
                  className="inline-flex items-center gap-3 border border-[#10B981]/40 bg-[#10B981]/10 px-5 py-4"
                  data-testid="download-waitlist-success"
                >
                  <BellRinging size={20} weight="fill" className="text-[#10B981]" />
                  <span className="text-[14px]">
                    You&rsquo;re on the list. We&rsquo;ll email you the day the {OS_LABEL[os]} build lands.
                  </span>
                </div>
              ) : (
                <form onSubmit={submitWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="download-waitlist-form">
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (status === "err") setStatus("idle"); }}
                    className="flex-1 bg-white/5 border border-white/15 text-white placeholder-[#64748B] px-4 py-3.5 text-[14px] outline-none focus:border-[#67E8F9]"
                    data-testid="download-waitlist-email"
                  />
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 text-white px-6 py-3.5 text-[14px] font-semibold"
                    data-testid="download-waitlist-submit"
                  >
                    <BellRinging size={16} weight="fill" />
                    {status === "submitting" ? "Adding you…" : "Notify me"}
                  </button>
                </form>
              )}
              {status === "err" && (
                <div className="mt-2 text-[12px] text-[#FCA5A5]" data-testid="download-waitlist-error">{errMsg}</div>
              )}
              <div className="mt-4 font-mono text-[11px] text-[#67E8F9] tracking-widest">
                DETECTED · {OS_LABEL[os].toUpperCase()} · WE&rsquo;LL SEND THE RIGHT BUILD
              </div>
            </div>

            {/* Fallback: use the web app today */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <div className="text-[13px] text-[#94A3B8]">Don&rsquo;t want to wait?</div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 mt-2 text-white font-semibold text-[15px] hover:text-[#67E8F9]"
                data-testid="download-webapp-cta"
              >
                Use Hello by Flow in your browser <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* App preview mock */}
          <div className="lg:col-span-5">
            <div className="border border-[#1E293B] bg-[var(--bg-dark)] shadow-2xl">
              <div className="px-3 py-2 border-b border-[#1E293B] flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                <span className="ml-3 font-mono text-[10px] text-[#94A3B8]">HELLO BY FLOW · PREVIEW</span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-[#67E8F9]" />
                  <div className="font-mono text-[10px] tracking-widest text-[#67E8F9]">COMING SOON</div>
                </div>
                <div className="font-display font-bold text-[26px] mt-3">Native desktop app</div>
                <div className="font-mono text-[12px] text-[#94A3B8] mt-1">macOS · Windows · Linux</div>
                <div className="mt-5 space-y-2 text-[12px] text-[#94A3B8]">
                  <div>• System-level incoming call alerts</div>
                  <div>• Native audio priority</div>
                  <div>• Bluetooth headset routing</div>
                  <div>• Same account as the web app</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why native */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1100px] mx-auto px-8 py-16">
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ WHY THE DESKTOP APP</div>
          <h2 className="font-display font-black text-[40px] leading-[1.0]">Built for people who live on calls.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] mt-10">
            {WHY.map((w) => (
              <div key={w.title} className="bg-white p-6">
                <w.icon size={22} weight="duotone" className="text-[var(--accent)]" />
                <div className="font-display font-bold text-[17px] mt-3">{w.title}</div>
                <div className="text-[13px] text-[var(--text-secondary)] mt-2 leading-relaxed">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All platforms — locked state */}
      <section id="all-platforms" className="border-b border-[var(--border)]">
        <div className="max-w-[1100px] mx-auto px-8 py-16">
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ ALL PLATFORMS</div>
          <h2 className="font-display font-black text-[40px] leading-[1.0] mb-3">Every OS. When it&rsquo;s ready.</h2>
          <p className="text-[14px] text-[var(--text-secondary)] mb-10 max-w-2xl">
            When we ship, installers will be hosted on Hello by Flow infrastructure — code-signed, notarised
            (macOS), SmartScreen-registered (Windows), and delivered from <span className="font-mono text-[12px]">downloads.hellobyflow.com</span>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)]">
            <PlatformCard icon={AppleLogo}   name="macOS"   version="Universal (Intel + Apple Silicon)" requires="macOS 10.15 Catalina or newer" testid="platform-mac" />
            <PlatformCard icon={WindowsLogo} name="Windows" version="x64 (64-bit)"                       requires="Windows 10 or 11"              testid="platform-win" />
            <PlatformCard icon={LinuxLogo}   name="Linux"   version="x64"                                requires="Ubuntu 22.04+ / Debian 12+ / Fedora 38+" testid="platform-linux" />
          </div>

          <div className="mt-10 p-6 border border-dashed border-[var(--border)] bg-[var(--bg-sidebar)]">
            <div className="font-mono text-[11px] tracking-widest text-[var(--text-muted)]">AVAILABLE TODAY</div>
            <div className="font-display font-bold text-[18px] mt-1">Use Hello by Flow in your browser</div>
            <p className="text-[13px] text-[var(--text-secondary)] mt-1">Every feature works on web today. The desktop app is a thin wrapper around the same React app — nicer for daily use, but not required.</p>
            <Link to="/login" className="inline-flex items-center gap-2 mt-3 text-[var(--accent)] font-semibold text-[14px]" data-testid="download-open-webapp">
              Open the web app <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-8">
        <div className="max-w-[1100px] mx-auto px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-[11px] tracking-widest text-[var(--text-muted)]">© 2026 HELLO BY FLOW</div>
          <div className="flex gap-6 text-[12px] text-[var(--text-secondary)]">
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/compliance">Compliance</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const WHY = [
  { icon: BellRinging, title: "Always-on incoming calls", desc: "OS-level push so an inbound call rings even when the browser is closed." },
  { icon: Lightning,   title: "Snappier audio",           desc: "Browsers throttle background tabs. The desktop app gets full system priority." },
  { icon: ShieldCheck, title: "Bluetooth headset reliability", desc: "Native audio routing means your AirPods / Jabra / Plantronics just work." },
  { icon: Globe,       title: "Same app, same data",      desc: "Logs into the same Hello by Flow workspace as the web. Switch seamlessly between devices." },
];

function PlatformCard({ icon: Icon, name, version, requires, testid }) {
  return (
    <div className="bg-white p-7 flex flex-col" data-testid={testid}>
      <Icon size={44} weight="duotone" className="text-[var(--accent)] opacity-60" />
      <div className="font-display font-extrabold text-[28px] mt-4">{name}</div>
      <div className="font-mono text-[11px] text-[var(--text-muted)] tracking-widest mt-1">{version.toUpperCase()}</div>
      <div className="text-[13px] text-[var(--text-secondary)] mt-3">Requires {requires}</div>
      <div
        className="mt-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[var(--border)] bg-[var(--bg-sidebar)] text-[var(--text-secondary)] text-[13px] font-semibold cursor-not-allowed select-none"
        aria-disabled="true"
        data-testid={`${testid}-status`}
      >
        <Clock size={14} /> Coming soon
      </div>
    </div>
  );
}
